import type { Msg, Simulation } from "rujira.js";
import type { V2Call } from "../../redacted/v2/action";
import { base64Decode } from "../../redacted/v2/encoding";
import { privateIntent } from "../../redacted/v2/fees";
import { privateFeeQuoteFromSimulation } from "../../redacted/v2/reviewedSimulation";

const record = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/** The one action of a contract message, e.g. `{ "order": … }`. */
const soleEntry = (value: unknown): [string, unknown] | undefined => {
  const object = record(value);
  const keys = object ? Object.keys(object) : [];
  return keys.length === 1 ? [keys[0], object![keys[0]]] : undefined;
};

const contractMessage = (msg: string): unknown => {
  try {
    return JSON.parse(new TextDecoder().decode(base64Decode(msg)));
  } catch {
    return undefined;
  }
};

/** A contract call that only takes the account's own funds out of a contract:
 * it attaches no funds and places or cancels nothing. These are the bodies the
 * order tables (limit, range, recurring) build for their claim controls. */
const claimOnly = (call: V2Call): boolean => {
  if (!("wasm_execute" in call) || call.wasm_execute.funds.length > 0)
    return false;
  const [action, body] =
    soleEntry(contractMessage(call.wasm_execute.msg)) ?? [];
  switch (action) {
    // FIN: a null target claims an order's filled amount and leaves the order
    // as it is ("0" would cancel it). No callback.
    case "order":
      return (
        Array.isArray(body) &&
        body.length === 2 &&
        body[1] === null &&
        Array.isArray(body[0]) &&
        body[0].length > 0 &&
        body[0].every(
          (order) =>
            Array.isArray(order) && order.length === 3 && order[2] === null
        )
      );
    // FIN range: claims the fees a liquidity range has earned.
    case "range":
      return soleEntry(body)?.[0] === "claim";
    // Recurring order: withdraws the named balances to its owner (the claim
    // control names the received asset).
    case "withdraw":
      return (
        Array.isArray(body) &&
        body.length > 0 &&
        body.every((coin) => {
          const fields = record(coin);
          return (
            typeof fields?.denom === "string" &&
            typeof fields.amount === "string"
          );
        })
      );
    default:
      return false;
  }
};

/** A claim is recognised from the exact calls that will run, never from a
 * label, tooltip or empty funds list: every call must be a known claim-only
 * contract call. Anything else, including a claim batched with another call,
 * keeps the confirmation. */
export const isPrivateClaim = (msg: Msg | null | undefined): boolean => {
  if (!msg) return false;
  try {
    const intent = privateIntent(msg);
    return intent.kind === "call" && intent.calls.every(claimOnly);
  } catch {
    return false;
  }
};

/** Only a claim (see `isPrivateClaim`) with a complete, current zero-debit fee
 * quote may bypass the extra review. Missing/malformed data is not a zero fee.
 * The session still validates and consumes the exact quote before
 * broadcasting. */
export const isZeroFeePrivateClaim = (simulation: Simulation): boolean => {
  const quote = privateFeeQuoteFromSimulation(simulation);
  const invalid = () => new Error("Unable to verify the claim fee. Try again.");
  if (
    !quote ||
    !quote.id.trim() ||
    quote.expiresAt <= Date.now() ||
    quote.source !== "spending" ||
    !["additional", "deducted"].includes(quote.protocolMode) ||
    typeof simulation.amount !== "bigint" ||
    simulation.amount < 0n
  )
    throw invalid();
  const validateCoins = (coins: unknown): boolean => {
    if (!Array.isArray(coins)) throw invalid();
    let zero = true;
    for (const coin of coins) {
      if (
        !coin ||
        typeof coin.denom !== "string" ||
        !coin.denom.trim() ||
        typeof coin.amount !== "string" ||
        !/^\d+$/.test(coin.amount)
      )
        throw invalid();
      if (BigInt(coin.amount) !== 0n) zero = false;
    }
    return zero;
  };
  // Validate every field even when an earlier field already contains a fee.
  const feesAreZero = validateCoins(quote.protocolFees);
  const relayIsZero = validateCoins(
    quote.relayerFee === undefined ? [] : [quote.relayerFee]
  );
  const amountsAreZero = validateCoins(quote.amounts);
  const debitIsZero = validateCoins(quote.totalDebit);
  return (
    simulation.amount === 0n &&
    feesAreZero &&
    relayIsZero &&
    amountsAreZero &&
    debitIsZero
  );
};
